/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Ref } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { Flex } from '../UNSAFE_Flex';
import { MessageBannerVariantOptions } from '../UNSAFE_MessageBanner';
import { MessageBannerRedwoodTheme } from '../UNSAFE_MessageBanner/themes/redwood/MessageBannerTheme';
import { MessageToastItem } from '../UNSAFE_MessageToast';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { Timer } from '../utils/PRIVATE_timer';
import { classNames as cx } from '../utils/UNSAFE_classNames';
import { Item } from '../utils/UNSAFE_dataProvider';
import { MessageRendererDataItem, MessageSeverity } from './Message.types';
import { MessageCloseButton } from './MessageCloseButton';
import { MessageCustomStartIcon } from './MessageCustomStartIcon';
import { MessageDetail } from './MessageDetail';
import { isValidValueForProp } from './MessageFormattingUtils';
import { MessageHeader } from './MessageHeader';
import { MessageStartIcon } from './MessageStartIcon';
import { MessageSummary } from './MessageSummary';
import { MessageTimestamp } from './MessageTimestamp';
import { playSound, severityBasedStyleClass } from './MessageUtils';
import { messageStyles } from './themes/MessageStyles.css';
import { xUnits } from '#UNSAFE_Theme';
import { Size } from '#utils/UNSAFE_size';

// TODO: Refactor Message Item types
// 

/**
 * An object representing a single message in Message component.
 */
export type MessageItem = {
  /**
   * Defines whether or not to include the close icon for the message
   *
   * @default 'on'
   */
  closeAffordance?: 'on' | 'off';

  /**
   * Defines the detail text of the message
   */
  detail?: string;

  /**
   * Defines the severity of the message
   */
  severity?: MessageSeverity;

  /**
   * Defines the sound to be played when opening the message
   */
  sound?: 'default' | 'none' | string;

  /**
   * Defines the primary text of the message
   */
  summary?: string;

  /**
   * Defines the timestamp for the message in ISO format
   */
  timestamp?: string;
};

/**
 * Props for the Message Component
 */
type Props<Key, Data> = {
  /**
   * A ref object for holding reference to this component
   */
  messageRef?: Ref<HTMLDivElement>;

  /**
   * The message item with all the necessary data in it
   */
  item: Item<Key, Data>;

  /**
   * A custom renderer for rendering the detail content
   */
  detailRenderer?: (item: MessageRendererDataItem<Key, Data>) => ComponentChildren;

  /**
   * A custom renderer for rendering the start icon
   */
  iconRenderer?: (item: MessageRendererDataItem<Key, Data>) => ComponentChildren;

  /**
   * Triggered when a user tries to close a message through UI interaction. The parent
   * should listen to this event and remove the corresponding message item from the data
   * which would then result in the message to be removed from the DOM. If the parent
   * fails to remove the message item from the data, then no change will be done in the
   * UI by the component.
   */
  onClose?: (item: Item<Key, Data>) => void;

  /**
   * A message can be a Banner message or Toast message.
   *
   * Furthermore, a Banner message can have a different look and feel. For example, when using page-level
   * messaging the messages need to be rendered from edge to edge without any outline. On the other
   * hand, when they are being used in a section of a page or a dialog, they need to be rendered
   * with an outline. This attribute can be used to specify where the component is being used so that
   * it will render the messages accordingly.
   */
  variant?: 'pageBanner' | 'sectionBanner' | 'toast';
};

/**
 * Determines the base variant of the message - banner or toast
 *
 * @param variant The message variant
 * @returns The base variant
 */
function getBaseVariant(variant: 'pageBanner' | 'sectionBanner' | 'toast') {
  if (variant === 'pageBanner' || variant === 'sectionBanner') {
    return 'banner';
  }
  return 'toast';
}

/**
 * Determines if a severity icon is needed based on the component severity
 *
 * @param severity The component severity
 * @returns Whether or not to render the severity icon
 */
function isSeverityIconNeeded(
  severity: MessageSeverity
): severity is Exclude<MessageSeverity, 'none'> {
  const isValidSeverity = isValidValueForProp(severity, 'severity');
  return isValidSeverity && severity !== 'none';
}

/**
 * Default timeout duration for autoTimeout in milliseconds
 */
const MIN_SECONDS = 5;
const DEFAULT_TIMEOUT = MIN_SECONDS * 1000;

/**
 * Component that renders an individual message
 */
function Message<K extends string | number = string | number, D extends MessageItem = MessageItem>({
  detailRenderer,
  iconRenderer,
  item,
  onClose,
  messageRef = () => {},
  variant = 'pageBanner'
}: Props<K, D>) {
  const { closeAffordance = 'on', severity = 'error', sound, summary, timestamp } = item.data;
  const { autoTimeout = 'off' } = item.data as MessageToastItem;
  const baseVariant = getBaseVariant(variant);

  /**
   * Handles clicking on the close icon of the message
   */
  const handleClose = useCallback(() => {
    onClose?.(item);
  }, [item, onClose]);

  /**
   * Handles closing the message on pressing Esc
   */
  const handleCloseOnEsc = useCallback(
    (event: KeyboardEvent) => {
      // Close the message only when closeAffordance is on
      if (event.key === 'Escape' && closeAffordance === 'on') {
        onClose?.(item);
      }
    },
    [closeAffordance, item, onClose]
  );

  /**
   * Set auto timeout for toast messages
   */
  const shouldTimeout = baseVariant === 'toast' && autoTimeout !== 'off';
  const currentTimerRef = useRef<Timer>();
  const timeout = typeof autoTimeout === 'number' ? autoTimeout : DEFAULT_TIMEOUT;
  const timeoutDuration = autoTimeout === 'on' ? DEFAULT_TIMEOUT : timeout;

  const startTimer = useCallback(() => {
    if (currentTimerRef.current) return;

    currentTimerRef.current = new Timer(handleClose, timeoutDuration);
  }, [handleClose, timeoutDuration]);

  const clearTimer = useCallback(() => {
    if (!currentTimerRef.current) return;

    currentTimerRef.current.clear();
    currentTimerRef.current = undefined;
  }, []);

  const pauseTimer = useCallback(() => {
    currentTimerRef.current?.pause();
  }, []);

  const resumeTimer = useCallback(() => {
    currentTimerRef.current?.resume();
  }, []);

  useEffect(() => {
    if (isValidValueForProp(sound)) {
      // It is sufficient to check for the value to be a
      // non-empty string. The playSound method takes care of the rest.
      playSound(sound);
    }

    // on mount, start timer for toast messages if needed
    if (shouldTimeout) {
      startTimer();
    }

    return () => {
      // clear out any pending timer
      clearTimer();
    };
    // eslint-disable-next-line
  }, []); // No deps to run this only on mount

  const { variantClasses } = useComponentTheme<MessageBannerVariantOptions>(
    MessageBannerRedwoodTheme,
    {
      severity
    }
  );

  // TODO:  Refactor to utilize useComponentTheme and multiVariantStyles.
  const rootClasses = cx([
    baseVariant === 'banner' && variantClasses,
    messageStyles.base[baseVariant],
    // for now, we do not have severity based styling on toast. So, we ignore this class for toasts
    variant !== 'toast' && severityBasedStyleClass(severity, baseVariant),
    variant === 'sectionBanner' && messageStyles.section
  ]);

  const contentClasses = cx([messageStyles.content.base, messageStyles.content[baseVariant]]);

  // We will be animating the root div, so add padding to an inner wrapper div so that
  // when animating height looks smooth. If padding were to be added to the root
  // div, the animation will not be smooth as height will never reach 0 due to the
  // padding.
  return (
    <div
      ref={messageRef}
      class={rootClasses}
      role="alert"
      aria-atomic="true"
      data-oj-key={`${typeof item.key}-${item.key}`}
      tabIndex={0}
      onKeyUp={handleCloseOnEsc}
      onFocusIn={pauseTimer}
      onFocusOut={resumeTimer}>
      <div class={contentClasses}>
        {iconRenderer ? (
          <MessageCustomStartIcon item={item} renderer={iconRenderer} />
        ) : isSeverityIconNeeded(severity) ? (
          <MessageStartIcon severity={severity} variant={baseVariant} />
        ) : null}
        <Flex direction="column" flex="1" gap={xUnits(2) as Size}>
          <MessageHeader variant={baseVariant}>
            <MessageSummary variant={baseVariant} text={summary} role="heading" aria-level={2} />
            {isValidValueForProp(timestamp, 'timestamp') && variant !== 'toast' && (
              <MessageTimestamp variant={baseVariant} value={timestamp!} />
            )}
          </MessageHeader>
          <MessageDetail variant={baseVariant} item={item} renderer={detailRenderer} />
        </Flex>
        {closeAffordance === 'on' && (
          <MessageCloseButton variant={baseVariant} onAction={handleClose} />
        )}
      </div>
    </div>
  );
}

export { Message };
